import { transaction } from "$lib/util/db";
import { obsUntilOnline } from "$lib/util/window";
import { catchError, EMPTY, from, map, merge, Observable, of, startWith, switchMap, tap } from "rxjs";
import { get$, post$ } from "./auth";
import { getItem } from "./local-store";
import { captureMessage, captureException } from "@sentry/sveltekit";
import { extractErrorMessage } from "$lib/util/format";
import { debugCache } from "./conf";

export type Cache<T> = {
	/**
	 * Are more values on the way?
	 */
	loading?: boolean,
	
	/**
	 * One of three responses can be expected (and always in the order). If young cache is
	 * found only `cache` will be streamed. If stale cache is found `cache` and `fresh`
	 * will be streamed.
	 */
	type?: 'cache' | 'offlinewait' | 'fresh',

	/**
	 * Valid cached or fresh payload.
	 */
	payload?: T,

	/**
	 * Any caught errors in the stream will produce an error.
	 */
	error?: any,
}

export type CacheOptions = {
	/**
	 * The database or promise of.
	 */
	db: IDBDatabase | Promise<IDBDatabase | null>,

	/**
	 * The name of the object store the cache will be stored in. This store has to have the
	 * name `path` as the first key path (the URL `pathname` will be the value). Default is
	 * `cache`.
	 */
	storeName?: string,

	/**
	 * The number of milliseconds cache will be valid for. A zero or negative value will
	 * result in cache being ignored forcing a fetch of fresh data.
	 */
	msMaxCacheAge: number,
}

export type KeyMap<Type> = {
	/**
	 * The key name used as part of the IndexedDB path.
	 */
	key: keyof Type,

	/**
	 * The query parameter name or the path to a payload value.
	 */
	path: string[],
}

let untilOnline = obsUntilOnline; // for mocking in tests

/**
 * A simple cached fetch. If the last fetch of a specific path and search parameter set
 * has young stored data the stored data will be returned.
 * @param url The request URL of which the data need to be cached.
 * @param keyPaths An ordered list (array) of keys each with a mapping to a query
 * parameter or a property in the post body. If an element in the array is an array the
 * first element is the key and the second element is the path to the property in the
 * query string or post body. If an element is a string it is assumed to be the key and
 * the path to the property is the same as the key.
 * @param cacheOptions Extra properties required to cache data.
 * @param postBody An optional payload of a post request. If set then a POST will be
 * performed instead of a GET. Normally a restful POST request should not be cached, but
 * in the case of server-side search endpoints the POST body is really just bundled
 * query parameters.
 * @param onSaved A callback when fresh data has been received and cache updated.
 * @param debug If `true` details of composite data is printed to the console.
 * @param refresher An observable that will trigger a fresh fetch. `true` will ignore the
 * cache and `false` will first consider the cache.
 * @returns A stream of cache objects with loading state and payload type. The last cache
 * object (as the observable completes) will have a loading state of `false`.
 */
export function fetch$<ResponseType, CacheType extends Cache<ResponseType> = any>(
	url: URL,
	keyPaths: (KeyMap<CacheType> | [string, string | string[]] | string)[],
	cacheOptions: CacheOptions,
	postBody?: unknown,
	onSaved?: (payload:ResponseType, cacheKey:IDBValidKey) => void,
	refresher: Observable<boolean> = EMPTY,
	debug?: boolean,
):Observable<Cache<ResponseType>> {
	debug = typeof debug === 'boolean' ? debug : debugCache.value();

	// break the url into components
	const { pathname, searchParams } = url;

	// create the IndexedDB object to be stored as the header for the cache payload
	const { row, keyPath, keys } = createIDBObject(keyPaths, pathname, searchParams, postBody)
	if (debug) console.debug('Cache: IndexedDB keyPath in use:', keyPath);
	// const strTimerLabel = `cache: ${keyPath.join()}`;

	const pdb = 'transaction' in cacheOptions.db ? Promise.resolve(cacheOptions.db) : cacheOptions.db;

	// prepare a fetch of fresh data that will also result in a database update
	const fetch$ = of(null).pipe(
		map(() => new Date()),
		switchMap(fetchedAt => {
			// setup request observable
			const req$ = postBody ? post$<ResponseType>(url.toString(), postBody, true) : get$<ResponseType>(url.toString(), true);
			return req$.pipe(map(payload => ({payload, fetchedAt})));
		}),
		// tap(() => console.timeLog(strTimerLabel, 'fetched')),
		// update IndexedDB cache payload - this is a fire-and-forget for performance reasons
		switchMap(async ({ payload, fetchedAt }) => {
			const db = await pdb;
			if (!db) return { payload };
			if (!payload) return { payload }; // prevent storing empty (200) payloads (proxy issue - talk to GW)
			const storename = cacheOptions.storeName ?? 'cache';
			const os = transaction(db, storename, 'readwrite').os(storename); // if this line errors please notify GW
			try {
				// save the params and payload to the database possibly replacing the existing entry
				const key = await os.put({ ...row, fetchedAt, payload });
				// register restaurants
				onSaved?.(payload, key);
				if (debug) console.debug('Cache: IndexedDB successfully put:', row);
			} catch (error) {
				console.warn(error)
				if (debug) console.debug('Cache: IndexedDB failed to put:', row);
			}

			return { payload };
			// console.timeLog(strTimerLabel, 'cached');
		}),
		map(({payload}) => ({ loading: false, type: 'fresh', payload } as Cache<ResponseType>)),
		catchError(er => of({ loading: false, type: 'fresh', error: er } as Cache<ResponseType>)),
	)

	/**
	 * Suggestion by Dan to add optional timeout functionality to the fetch.
	 */
	// with the fetch lets handle offline state gracefully
	const fresh$ = untilOnline().pipe(
		switchMap(online => online ? fetch$ : of({ loading: true, type: 'offlinewait' } as Cache<ResponseType>)),
	)

	const dbcache$ = from(pdb).pipe(
		// tap(() => console.time(strTimerLabel)),
		// get cache from the database
		switchMap(db => {
			if (!db) return of(`Database not provided.`);
			const storename = cacheOptions.storeName ?? 'cache';
			if (!db.objectStoreNames.contains(storename)) {
				return of(`ObjectStore ${storename} does not exist in database.`);
			}
			if (debug) console.debug(`Cache: Found IndexedDB objectStore "${storename}".`);
			const os = transaction(db, storename).os(storename);
			if (!Array.isArray(os.keyPath)) {
				return of(`The keyPath for ObjectStore "${storename}" is "${os.keyPath}", not [${keys.map(k => `"${k}"`).join(', ')}].`);
			}
			if (os.keyPath.join(',') !== keys.join(',')) {
				return of(`The keyPath for ObjectStore "${storename}" is [${os.keyPath.map(k => `"${k}"`).join(', ')}], not [${keys.map(k => `"${k}"`).join(', ')}].`);
			}
			const promiseCache = os.get<CacheType & { fetchedAt: number }>(keyPath);
			return from(promiseCache);
		}),
		tap(debug ? {
			next: res => {
				if (typeof res === 'string') {
					console.warn(`Cache: ${res}`)
				} else if (res) {
					console.log(`Cache: IndexedDB record loaded with properties:`, Object.keys(res))
				} else {
					console.log(`Cache: IndexedDB record not found.`)
				}
			},
			error: er => console.log('Cache: Unexpected IndexedDB', er),
		} : {}),
		tap({error: er => console.warn(`Cache error`, er)}),
		// any other error should be to technical for end-users and should not propagate to the UI, this will result in a no-payload cache value followed by an attempt at fetching fresh data
		catchError(er => of('Failed to load cache.')),
		// tap(() => console.timeLog(strTimerLabel, 'loaded')),
	);

	return refresher.pipe(
		startWith(false),

		switchMap(ignoreCache => {
			if (cacheOptions.msMaxCacheAge <= 0 || ignoreCache) {
				return of({ cache:null, ignoreCache });
			}
			return dbcache$.pipe(
				map(cache => ({ cache, ignoreCache: cacheOptions.msMaxCacheAge <= 0 || ignoreCache })),
			)
		}),

		// loaded cache has 4 possible outcomes
		switchMap(({cache, ignoreCache}) => {

			// 1. no cache
			if (!cache) {
				if (debug) {
					if (ignoreCache) {
						console.debug('Cache: Ignoring cache data, fresh data to follow.');
					} else {
						console.debug('Cache: Emitting no cache data, fresh data to follow.');
					}
				}
				return merge(of({ loading: true, type: 'cache' } as Cache<ResponseType>), fresh$);
			}

			// 2. cache load error
			if (typeof cache === 'string') {
				if (debug) console.debug('Cache: Emitting cache error, fresh data to follow.');
				return merge(of({ loading: true, type: 'cache', error: cache } as Cache<ResponseType>), fresh$);
			}
			
			// get the age of the cache
			const age = Date.now() - cache.fetchedAt;

			// 3. cache is STALE
			if (age > cacheOptions.msMaxCacheAge) {
				if (debug) console.debug('Cache: Emitting stale cache data, fresh data to follow.');
				return merge(of({ loading: true, type: 'cache', payload: cache.payload } as Cache<ResponseType>), fresh$);
			}

			// 4. cache is GOOD
			if (debug) console.debug('Cache: Emitting valid cache data, no fresh data needed.');
			return of({ loading: false, type: 'cache', payload: cache.payload } as Cache<ResponseType>)

		}),
		// finalize(() => console.timeEnd(strTimerLabel)),
		tap({
			error: er => captureException(extractErrorMessage(er, 'Cache error'), { extra: {
				url: url.toString(),
				keyPaths: JSON.stringify(keyPaths),
				postBody: JSON.stringify(postBody),
			}}),
		})
	)
}

/**
 * Takes API request details (URL and optional payload) and produces a valid object and
 * keyPath to store and index the request and response paylaod.
 * @param keyPaths A list of properties to use when building the `keyPath`.
 * @param pathname The pathname of the endpoint the request is made to.
 * @param searchParams The query parameters of the request.
 * @param postBody An optional POST payload.
 * @returns A object (`row`) ready to be stored in the IndexedDB and the unique IndexedDB
 * `keyPath`.
 */
export function createIDBObject<CacheType>(
	keyPaths: (KeyMap<CacheType> | [string, string | string[]] | string)[],
	pathname: string,
	searchParams: URLSearchParams,
	postBody?: any,
) {
	// prepare a database-ready row object
	const row = { path: pathname } as CacheType & { path: string };
	// perform a key test: all key lists need to have at least one key
	keyPaths.forEach((key,i) => {
		if (Array.isArray(key) && !key.length) throw new Error(`The provided keyPaths item at index ${i} requires at least one key.`);
	});

	// create a uniform key-value mapping
	const keyMaps = keyPaths.map(kp => {
		if (typeof kp === 'string') return { key: kp, path: [kp] } as KeyMap<CacheType>;
		if (Array.isArray(kp)) return { key:kp[0], path: typeof kp[1] === 'string' ? kp : kp[1] } as KeyMap<CacheType>;
		return kp;
	})

	// get a value for each key
	keyMaps.forEach(({ key, path }) => {
		let value: any = undefined;
		// FIRST attempt to get a post body value
		try {
			if (postBody) {
				value = path.reduce((value, k) => value[k], postBody);
				value = JSON.stringify(value);
			}
		} catch (error) {
			// postBody path-to-value evaluation failed
		}
		// ALTERNATIVELY attempt to get a query parameter value
		if (typeof value === 'undefined') {
			value = searchParams.get(path.find(p => searchParams.has(p)) ?? '') as any;
		}

		row[key] = value?.toString() ?? ''; // for the IndexedDB we really just want plain strings to match
	});

	// create IndexedDB keyPath keys
	const keys = ['path'].concat(keyMaps.map(km => km.key.toString()));
	// create IndexedDB keyPath
	const keyPath = keys.map(key => row[key as keyof CacheType]) as string[];

	return {
		/**
		 * The IndexdDB object/row representing a cache entry.
		 */
		row,

		/**
		 * The unique IndexedDB keyPath.
		 */
		keyPath,

		/**
		 * Extra debug info: the name of the properties used to build the keyPath.
		 */
		keys,
	}
}

export function mock(
	untilOnline$:Observable<boolean>
) {
	// if (env.TEST !== 'true') throw new Error('This function is only available in test environments.');
	untilOnline = () => untilOnline$;
}
